import { type ReactNode } from 'react';
import { Check } from 'lucide-react';

interface WizardOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

interface WizardStepProps {
  title: string;
  subtitle: string;
  options: WizardOption[];
  selectedValues: string[];
  onToggle: (value: string) => void;
  headerExtra?: ReactNode;
}

const WizardStep = ({ title, subtitle, options, selectedValues, onToggle, headerExtra }: WizardStepProps) => {
  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
          {title}
        </h2>
        <p className="mt-2 text-gray-500 dark:text-gray-400 text-sm sm:text-base">
          {subtitle}
        </p>
        {headerExtra}
      </div>

      {/* Options grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {options.map((option) => {
          const isSelected = selectedValues.includes(option.value);

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onToggle(option.value)}
              className={`
                relative group flex flex-col items-center gap-2 p-4 rounded-xl border-2
                transition-all duration-200 cursor-pointer
                focus:outline-none focus:ring-2 focus:ring-vivatis-500 focus:ring-offset-2
                dark:focus:ring-offset-gray-900
                ${
                  isSelected
                    ? 'border-vivatis-500 bg-vivatis-50 dark:bg-vivatis-950/40 dark:border-vivatis-500'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 dark:hover:bg-gray-750'
                }
              `}
            >
              {/* Checkmark badge */}
              {isSelected && (
                <div className="absolute top-2 right-2">
                  <div className="w-5 h-5 rounded-full bg-vivatis-600 flex items-center justify-center">
                    <Check size={12} strokeWidth={3} className="text-white" />
                  </div>
                </div>
              )}

              {/* Icon */}
              {option.icon && (
                <div
                  className={`
                    transition-colors duration-200
                    ${
                      isSelected
                        ? 'text-vivatis-600 dark:text-vivatis-400'
                        : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-500 dark:group-hover:text-gray-400'
                    }
                  `}
                >
                  {option.icon}
                </div>
              )}

              {/* Label */}
              <span
                className={`
                  text-sm font-medium text-center leading-tight transition-colors duration-200
                  ${
                    isSelected
                      ? 'text-vivatis-700 dark:text-vivatis-300'
                      : 'text-gray-700 dark:text-gray-300'
                  }
                `}
              >
                {option.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Selection count */}
      <div className="mt-6 text-center">
        <span className="text-sm text-gray-400 dark:text-gray-500">
          {selectedValues.length === 0
            ? 'No selection made'
            : `${selectedValues.length} selected`}
        </span>
      </div>
    </div>
  );
};

export default WizardStep;
